import type React from 'react';

import styled, { css } from 'styled-components';

import { colorsPalettes, setFontVariant } from '@similarweb/styles';

export type Placement = 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type TooltipVariant = 'default' | 'white';

const getArrowPlacementStyles = (placement: Placement, variant?: TooltipVariant) => {
  const borderColor = variant === 'white' ? colorsPalettes.carbon[0] : colorsPalettes.carbon[500];
  switch (placement) {
    case 'top':
      return css`
        bottom: -15px;
        left: calc(50% - 8px);
        border-top-color: ${borderColor};
      `;

    case 'bottom':
      return css`
        top: -15px;
        left: calc(50% - 8px);
        border-bottom-color: ${borderColor};
      `;

    case 'left':
      return css`
        right: -15px;
        top: calc(50% - 8px);
        border-left-color: ${borderColor};
      `;

    case 'right':
      return css`
        left: -15px;
        top: calc(50% - 8px);
        border-right-color: ${borderColor};
      `;

    case 'top-left':
      return css`
        bottom: -15px;
        right: 8px;
        border-top-color: ${borderColor};
      `;
    case 'top-right':
      return css`
        bottom: -15px;
        left: 8px;
        border-top-color: ${borderColor};
      `;
    case 'bottom-left':
      return css`
        top: -15px;
        right: 8px;
        border-bottom-color: ${borderColor};
      `;
    case 'bottom-right':
      return css`
        top: -15px;
        left: 8px;
        border-bottom-color: ${borderColor};
      `;
    default:
      return '';
  }
};

const getTooltipVariantStyles = (variant?: TooltipVariant) => {
  switch (variant) {
    case 'white':
      return css`
        background-color: ${colorsPalettes.carbon[0]};
        color: ${colorsPalettes.carbon[500]};
        border-radius: 20px;
        box-shadow: 0px 0px 20px 0px rgba(14, 30, 62, 0.22);
      `;
    default:
      return css`
        background-color: ${colorsPalettes.carbon[500]};
        color: ${colorsPalettes.bluegrey[100]};
        border-radius: 3px;
      `;
  }
};

export const TooltipContent = styled.div<{
  placement: Placement;
  minWidth: number;
  variant?: TooltipVariant;
  maxWidth?: number;
}>`
  background-color: ${colorsPalettes.carbon[500]};
  ${setFontVariant('tooltip', colorsPalettes.bluegrey[100])};
  padding: 8px 16px;
  border-radius: 3px;
  z-index: 10000;
  min-width: ${({ minWidth }) => minWidth}px;

  ${({ variant }) => getTooltipVariantStyles(variant)};
  ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth}px;` : '')}
  ${({ minWidth, maxWidth }) => (minWidth && maxWidth ? `width: max-content;` : '')}

  &::after {
    content: '';
    position: absolute;
    border-style: solid;
    border-color: transparent;
    border-width: 8px;
    ${({ placement, variant }) => getArrowPlacementStyles(placement, variant)}
  }
`;

export const TooltipWrapper = styled.div<{
  enabled: boolean;
  fullWidth?: boolean;
  cursor: React.CSSProperties['cursor'];
}>`
  position: relative;
  display: inline-flex;
  cursor: ${({ enabled, cursor }) => (enabled ? cursor : 'default')};
  ${({ fullWidth }) => (fullWidth ? 'width: 100%;' : '')}
`;
